import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {type FileAttachment} from './file-attachment';
import {FormSelectOption} from './form-select-option';
import {type ValueOf} from './util';

export const enum HTMLInputFieldTypes {
  BUTTON = 'button',
  CHECKBOX = 'checkbox',
  COLOR = 'color',
  DATE = 'date',
  DATETIME_LOCAL = 'datetime-local',
  EMAIL = 'email',
  FILE = 'file',
  HIDDEN = 'hidden',
  IMAGE = 'image',
  NUMBER = 'number',
  PASSWORD = 'password',
  RADIO = 'radio',
  RANGE = 'range',
  RESET = 'reset',
  SEARCH = 'search',
  SUBMIT = 'submit',
  TEL = 'tel',
  TEXT = 'text',
  TIME = 'time',
  URL = 'url',
}

export const enum FormlyFieldTypes {
  BOOLEAN = 'boolean',
  CHECKBOX = 'checkbox',
  DATEPICKER = 'datepicker',
  ENUM = 'enum',
  INPUT = 'input',
  INTEGER = 'integer',
  MULTICHECKBOX = 'multicheckbox',
  NUMBER = 'number',
  RADIO = 'radio',
  SELECT = 'select',
  SLIDER = 'slider',
  STRING = 'string',
  TEXTAREA = 'textarea',
  TOGGLE = 'toggle',
}

export const enum CustomFieldTypes {
  CATEGORY_TREE = 'categorytree',
  CHIP_LIST = 'chiplist',
  FILE_UPLOAD = 'fileupload',
  RICH_TEXT = 'richtext',
  TIMEPICKER = 'timepicker',
  REPEAT = 'repeat',
  HIPAA = 'hipaa',
}

export type HTMLInputFieldType = ValueOf<typeof HTMLInputFieldTypes>;
export type FormlyFieldType = ValueOf<typeof FormlyFieldTypes>;
export type CustomFieldType = ValueOf<typeof CustomFieldTypes>;
export type FormFieldType = FormlyFieldType | CustomFieldType;

export type BooleanFieldMode = 'checkbox' | 'slideToggle' | 'buttonToggle';

export interface ButtonToggleOptions {
  label: string;
  tooltip: string;
  icon: string;
  value: string;
  onClick?: () => Promise<boolean>;
}

export interface FormFieldProps {
  name?: string;
  fieldsetId?: string;
  fieldsetLabel?: string;
  formControl: UntypedFormControl | UntypedFormGroup;
  formGroup: UntypedFormGroup;
  label?: string;
  helpText?: string;
  hideNoneOption?: boolean;
  maxLength?: number;
  minLength?: number;
  multiSelect?: boolean;
  options?: object;
  placeholder?: string;
  required?: boolean;
  hidden?: boolean;
  showIcons?: boolean;
  type?: FormFieldType;
  showTime?: boolean;
  defaultValue?: unknown;
  markdownAbove?: string;
  markdownBelow?: string;
  tooltip?: string;
  booleanMode?: BooleanFieldMode;
  booleanOptions?: ButtonToggleOptions[];
  attachments?: Map<number | string, FileAttachment>;
  selectOptions?: string[] | FormSelectOption[];
  apiSource?: string;
  passwordsMatch?: boolean;
  onChange?: (event: Event) => void;
}

export class FormField implements FormFieldProps {
  name?: string;
  fieldsetId?: string;
  fieldsetLabel?: string;
  formControl: UntypedFormControl | UntypedFormGroup;
  formGroup: UntypedFormGroup;
  label?: string;
  helpText?: string;
  maxLength?: number;
  minLength?: number;
  multiSelect = false;
  options?: object;
  placeholder: string;
  required?: boolean;
  hidden?: boolean;
  showIcons = false;
  type: FormFieldType;
  showTime?: boolean;
  defaultValue?: unknown;
  markdownAbove?: string;
  markdownBelow?: string;
  tooltip?: string;
  booleanMode?: BooleanFieldMode;
  booleanOptions?: ButtonToggleOptions[];
  onChange?: (event: Event) => void;
  hideNoneOption?: boolean;

  // 'files' type
  attachments = new Map<number | string, FileAttachment>();

  // 'select' type can pull from a hard-coded list
  // or from API. If selectOptions is not provided,
  // apiSource will be used.
  selectOptions?: string[] | FormSelectOption[];
  apiSource?: string; // Should be the name of the API function to call
  passwordsMatch?: boolean;

  constructor(private _props: FormFieldProps) {
    for (const propName in this._props) {
      if (Object.hasOwn(this._props, propName) && ![null, undefined].includes(this._props[propName])) {
        this[propName] = this._props[propName];
      }
    }
  }
}
